import { motion } from 'framer-motion';
import { ScrambleText } from '@/components/ScrambleText';

const CODE_LINE = '01{fn:gallery}=>init(humanity)||exec[art.digital]&&render<perception>;const_belief=true;while(scroll){decode();}';

const bulletPoints = [
  'DIGITAL & AI ART',
  'IMMERSIVE VISUAL EXPERIENCES',
  'PHILOSOPHICAL & FUTURE-CULTURE NARRATIVES',
  'BRIDGING TECHNOLOGY AND HUMAN PERCEPTION',
];

export default function WhatIsSection() {
  return (
    <section
      data-testid="what-is-section"
      className="relative z-10 px-6 sm:px-10 py-20 lg:py-28"
    >
      <div className="overflow-hidden mb-16">
        <div className="code-divider-scroll whitespace-nowrap text-[10px] font-mono text-[#1a1a1a] select-none">
          {CODE_LINE} {CODE_LINE} {CODE_LINE} {CODE_LINE}
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <ScrambleText
            text="WHAT IS THE RELATIVE GALLERY?"
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit block"
            as="h2"
            duration={800}
            distortOnHover
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-12"
        >
          <div className="space-y-6">
            <p className="text-sm sm:text-base leading-relaxed font-mono text-[#f6f6f6]">
              THE RELATIVE GALLERY IS A NEW EXHIBITION SPACE & ARTIST RETREAT
              GROUNDED IN DIGITAL PRACTICE.
            </p>
            <p className="text-sm sm:text-base leading-relaxed font-mono text-[#f6f6f6]">
              WHERE ART SHAPED DIGITALLY CAN EXTEND BEYOND THE SCREEN AND WHERE
              ARTISTS CAN CONNECT OUTSIDE OF DIGITAL SYSTEMS.
            </p>
            <p className="text-sm sm:text-base leading-relaxed font-mono text-[#f6f6f6]">
              STRUCTURED FOR LONG-TERM DEVELOPMENT. SITUATED IN THE NATURAL
              COUNTRYSIDE OF WESTERN POLAND, 1.5 HOURS FROM BERLIN.
            </p>
          </div>

          <div className="space-y-4">
            {bulletPoints.map((point, i) => (
              <motion.div
                key={point}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-4 py-3 border-b border-[#1a1a1a]"
              >
                <span className="text-[10px] font-mono text-[#f6f6f6] tracking-wider">
                  0{i + 1}
                </span>
                <span className="text-xs sm:text-sm font-mono text-[#f6f6f6] tracking-wide">
                  {point}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 border border-[#222222] p-8 sm:p-12 bg-[#0a0a0a]"
        >
          <ScrambleText
            text="THIS IS NOT JUST A GALLERY."
            className="text-lg sm:text-xl lg:text-2xl font-medium font-outfit text-white tracking-tight block"
            as="p"
            duration={600}
            distortOnHover
          />
          <ScrambleText
            text="THIS IS A SPACE FOR REFLECTION, DIALOGUE, & NEW WAYS OF SEEING."
            className="text-lg sm:text-xl lg:text-2xl font-medium font-outfit text-[#f6f6f6] tracking-tight block mt-2"
            as="p"
            delay={200}
            duration={600}
            distortOnHover
          />
        </motion.div>
      </div>
    </section>
  );
}
